import React, { useState } from 'react';
import { Layout } from '../../components/Layout';
import { Button } from '../../components/ui/Button';
import { Card } from '../../components/ui/Card';
import { CallbackModal } from '../../components/CallbackModal';
import { Calendar, Clock, Tag, ChevronLeft, MessageCircle, Plus, Minus, CheckCircle2, XCircle } from 'lucide-react';
import { Link } from 'react-router-dom';
const BOOKING_URL = 'https://denticoclinic.patientportal.onl/bookings';
const WHATSAPP_URL = 'https://wa.me/447345197820';
function AccordionItem({
  question,
  answer,
  isOpen,
  onToggle
}: {
  question: string;
  answer: string;
  isOpen: boolean;
  onToggle: () => void;
}) {
  return <div className="border-b border-gray-100 last:border-b-0" data-id="element-683">
      <button onClick={onToggle} className="w-full flex items-center justify-between py-4 text-left focus:outline-none group" data-id="element-684">
        <span className="text-lg font-medium text-gray-900 group-hover:text-teal-600 transition-colors" data-id="element-685">
          {question}
        </span>
        <span className={`flex-shrink-0 ml-4 w-6 h-6 rounded-full flex items-center justify-center transition-colors ${isOpen ? 'bg-teal-600 text-white' : 'bg-gray-100 text-gray-400'}`} data-id="element-686">
          {isOpen ? <Minus className="w-4 h-4" data-id="element-687" /> : <Plus className="w-4 h-4" data-id="element-688" />}
        </span>
      </button>
      <div className={`overflow-hidden transition-all duration-300 ease-in-out ${isOpen ? 'max-h-96 opacity-100 pb-4' : 'max-h-0 opacity-0'}`} data-id="element-689">
        <p className="text-gray-600 leading-relaxed" data-id="element-690">{answer}</p>
      </div>
    </div>;
}
export function NhsVsPrivateArticle() {
  const [isCallbackOpen, setIsCallbackOpen] = useState(false);
  const [openFaqIndex, setOpenFaqIndex] = useState<number | null>(0);
  const toggleFaq = (index: number) => {
    setOpenFaqIndex(openFaqIndex === index ? null : index);
  };
  const faqs = [{
    q: 'Чи безкоштовна NHS стоматологія?',
    a: 'Ні, для більшості дорослих вона платна, але субсидована. Безкоштовно лікуються лише діти, вагітні, матері немовлят та люди з певними пільгами (benefits).'
  }, {
    q: 'Чи можу я піти до приватного лікаря, якщо я зареєстрований в NHS?',
    a: 'Так, ви можете вільно обирати між NHS та приватною медициною. Реєстрація в NHS не обмежує вас у відвідуванні приватних клінік.'
  }, {
    q: 'Чому в NHS такі довгі черги?',
    a: 'Через велике навантаження та нестачу фінансування. Багато клінік NHS не приймають нових пацієнтів, а час очікування на прийом може складати місяці.'
  }, {
    q: 'Чи краща якість у приватних клініках?',
    a: 'Приватні клініки зазвичай мають новіше обладнання, більше часу на пацієнта та використовують кращі матеріали (наприклад, білі пломби замість амальгами).'
  }, {
    q: 'Чи покриває NHS чистку зубів (гігієну)?',
    a: "Лише якщо це клінічно необхідно для здоров'я ясен. Естетична чистка (Airflow) доступна тільки приватно."
  }, {
    q: 'Скільки коштує приватний огляд?',
    a: 'Вартість варіюється від £40 до £100 залежно від клініки. У Dentico Clinic ми пропонуємо прозорі ціни та детальну діагностику.'
  }];
  // Schemas
  const articleSchema = {
    '@context': 'https://schema.org',
    '@type': 'BlogPosting',
    headline: 'NHS vs приватна стоматологія у Великобританії — що обрати?',
    datePublished: '2026-02-12',
    author: {
      '@type': 'Organization',
      name: 'Dentico Clinic'
    },
    publisher: {
      '@type': 'Organization',
      name: 'Dentico Clinic',
      logo: {
        '@type': 'ImageObject',
        url: 'https://denticoclinic.co.uk/logo.png'
      }
    },
    description: 'Порівняння державної (NHS) та приватної стоматології в UK. Ціни, черги, якість та поради для українців.',
    inLanguage: 'uk'
  };
  const faqSchema = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: faqs.map(faq => ({
      '@type': 'Question',
      name: faq.q,
      acceptedAnswer: {
        '@type': 'Answer',
        text: faq.a
      }
    }))
  };
  return <Layout data-id="element-691">
      <CallbackModal isOpen={isCallbackOpen} onClose={() => setIsCallbackOpen(false)} data-id="element-692" />

      {/* Structured Data */}
      <script type="application/ld+json" dangerouslySetInnerHTML={{
      __html: JSON.stringify(articleSchema)
    }} data-id="element-693" />
      <script type="application/ld+json" dangerouslySetInnerHTML={{
      __html: JSON.stringify(faqSchema)
    }} data-id="element-694" />

      <article className="bg-white" data-id="element-695">
        {/* Header */}
        <div className="bg-teal-50 py-12 lg:py-20" data-id="element-696">
          <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-697">
            <Link to="/ua/blog" className="inline-flex items-center text-teal-600 font-medium mb-6 hover:underline" data-id="element-698">
              <ChevronLeft className="w-4 h-4 mr-1" data-id="element-699" />
              Назад до блогу
            </Link>

            <div className="flex flex-wrap gap-4 mb-6 text-sm text-gray-600" data-id="element-700">
              <span className="bg-white px-3 py-1 rounded-full font-medium text-teal-700 flex items-center shadow-sm" data-id="element-701">
                <Tag className="w-3 h-3 mr-2" data-id="element-702" />
                Гід пацієнта
              </span>
              <span className="flex items-center" data-id="element-703">
                <Calendar className="w-4 h-4 mr-2" data-id="element-704" />
                Лютий 2026
              </span>
              <span className="flex items-center" data-id="element-705">
                <Clock className="w-4 h-4 mr-2" data-id="element-706" />
                10 хв читання
              </span>
            </div>

            <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-gray-900 leading-tight mb-6" data-id="element-707">
              NHS vs приватна стоматологія у Великобританії — що обрати?
            </h1>

            <div className="flex items-center" data-id="element-708">
              <div className="w-10 h-10 bg-teal-200 rounded-full flex items-center justify-center text-teal-700 font-bold mr-3" data-id="element-709">
                DC
              </div>
              <div data-id="element-710">
                <p className="font-medium text-gray-900" data-id="element-711">Dentico Clinic Team</p>
                <p className="text-sm text-gray-500" data-id="element-712">Експертна стаття</p>
              </div>
            </div>
          </div>
        </div>

        {/* Content */}
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-12 lg:py-16" data-id="element-713">
          <div className="prose prose-lg prose-teal max-w-none text-gray-600" data-id="element-714">
            <p className="lead text-xl text-gray-800 font-medium mb-8" data-id="element-715">
              Для багатьох українців, які переїхали до Великобританії, місцева
              система охорони здоров'я може здаватися заплутаною. Особливо це
              стосується стоматології, де існує чіткий поділ на державну (NHS)
              та приватну (Private). Що обрати? Де краще лікувати зуби?
              Розбираємося в деталях.
            </p>

            <h2 className="text-2xl font-bold text-gray-900 mt-10 mb-6" data-id="element-716">
              Що таке NHS стоматологія?
            </h2>
            <p className="mb-6" data-id="element-717">
              NHS (National Health Service) — це державна система охорони
              здоров'я. Стоматологія в рамках NHS не є повністю безкоштовною для
              більшості дорослих, але ціни тут фіксовані та субсидовані
              державою.
            </p>
            <p className="mb-6" data-id="element-718">
              <strong data-id="element-719">Ціни NHS (Band Charges) у 2025/2026:</strong>
            </p>
            <ul className="space-y-3 mb-8" data-id="element-720">
              <li className="bg-gray-50 p-4 rounded-lg" data-id="element-721">
                <strong data-id="element-722">Band 1 (~£26.80):</strong> Огляд, діагностика, рентген,
                проста чистка (якщо необхідно), поради.
              </li>
              <li className="bg-gray-50 p-4 rounded-lg" data-id="element-723">
                <strong data-id="element-724">Band 2 (~£73.50):</strong> Все з Band 1 + пломби,
                видалення зубів, лікування каналів.
              </li>
              <li className="bg-gray-50 p-4 rounded-lg" data-id="element-725">
                <strong data-id="element-726">Band 3 (~£319.10):</strong> Все з Band 1 і 2 + коронки,
                мости, протези.
              </li>
            </ul>
            <p className="text-sm text-gray-500 italic mb-8" data-id="element-727">
              *Ціни можуть змінюватися, перевіряйте актуальні дані на сайті NHS.
            </p>

            <h2 className="text-2xl font-bold text-gray-900 mt-10 mb-6" data-id="element-728">
              Проблеми NHS стоматології
            </h2>
            <p className="mb-6" data-id="element-729">
              Хоча ціни виглядають привабливо, система має суттєві недоліки:
            </p>
            <ul className="space-y-2 mb-8" data-id="element-730">
              <li className="flex items-start" data-id="element-731">
                <XCircle className="w-5 h-5 text-red-500 mr-2 mt-1 flex-shrink-0" data-id="element-732" />
                <span data-id="element-733">
                  <strong data-id="element-734">Довгі черги:</strong> Очікування на плановий прийом
                  може тривати місяці.
                </span>
              </li>
              <li className="flex items-start" data-id="element-735">
                <XCircle className="w-5 h-5 text-red-500 mr-2 mt-1 flex-shrink-0" data-id="element-736" />
                <span data-id="element-737">
                  <strong data-id="element-738">Реєстрація:</strong> Знайти клініку, яка приймає нових
                  NHS пацієнтів, зараз дуже складно.
                </span>
              </li>
              <li className="flex items-start" data-id="element-739">
                <XCircle className="w-5 h-5 text-red-500 mr-2 mt-1 flex-shrink-0" data-id="element-740" />
                <span data-id="element-741">
                  <strong data-id="element-742">Обмеження матеріалів:</strong> NHS використовує базові
                  матеріали (наприклад, срібні амальгамні пломби на жувальні
                  зуби замість білих).
                </span>
              </li>
              <li className="flex items-start" data-id="element-743">
                <XCircle className="w-5 h-5 text-red-500 mr-2 mt-1 flex-shrink-0" data-id="element-744" />
                <span data-id="element-745">
                  <strong data-id="element-746">Час на пацієнта:</strong> Прийом часто короткий (15-20
                  хв), лікар не має часу на детальне пояснення.
                </span>
              </li>
            </ul>

            <h2 className="text-2xl font-bold text-gray-900 mt-10 mb-6" data-id="element-747">
              Приватна стоматологія: переваги
            </h2>
            <p className="mb-6" data-id="element-748">
              Приватні клініки працюють незалежно від держави. Ви платите повну
              вартість лікування, але отримуєте зовсім інший рівень сервісу.
            </p>
            <ul className="space-y-3 mb-8" data-id="element-749">
              <li className="flex items-start" data-id="element-750">
                <CheckCircle2 className="w-5 h-5 text-teal-600 mr-3 mt-1 flex-shrink-0" data-id="element-751" />
                <span data-id="element-752">
                  <strong data-id="element-753">Швидкість:</strong> Прийом можна отримати в той же
                  день або на наступний.
                </span>
              </li>
              <li className="flex items-start" data-id="element-754">
                <CheckCircle2 className="w-5 h-5 text-teal-600 mr-3 mt-1 flex-shrink-0" data-id="element-755" />
                <span data-id="element-756">
                  <strong data-id="element-757">Естетика:</strong> Використовуються сучасні
                  фотополімерні (білі) пломби, кераміка, вініри.
                </span>
              </li>
              <li className="flex items-start" data-id="element-758">
                <CheckCircle2 className="w-5 h-5 text-teal-600 mr-3 mt-1 flex-shrink-0" data-id="element-759" />
                <span data-id="element-760">
                  <strong data-id="element-761">Обладнання:</strong> Цифрові сканери, мікроскопи, КТ —
                  це стандарт для хорошої приватної клініки.
                </span>
              </li>
              <li className="flex items-start" data-id="element-762">
                <CheckCircle2 className="w-5 h-5 text-teal-600 mr-3 mt-1 flex-shrink-0" data-id="element-763" />
                <span data-id="element-764">
                  <strong data-id="element-765">Час та увага:</strong> Лікар приділяє вам стільки
                  часу, скільки потрібно (30-60 хв).
                </span>
              </li>
            </ul>

            <h2 className="text-2xl font-bold text-gray-900 mt-10 mb-6" data-id="element-766">
              Порівняльна таблиця
            </h2>
            <div className="overflow-x-auto mb-8" data-id="element-767">
              <table className="w-full text-left border-collapse" data-id="element-768">
                <thead data-id="element-769">
                  <tr className="bg-gray-100" data-id="element-770">
                    <th className="p-4 border border-gray-200" data-id="element-771">Критерій</th>
                    <th className="p-4 border border-gray-200" data-id="element-772">NHS</th>
                    <th className="p-4 border border-gray-200" data-id="element-773">
                      Приватна (Private)
                    </th>
                  </tr>
                </thead>
                <tbody data-id="element-774">
                  <tr data-id="element-775">
                    <td className="p-4 border border-gray-200 font-medium" data-id="element-776">
                      Ціна
                    </td>
                    <td className="p-4 border border-gray-200" data-id="element-777">
                      Низька, фіксована
                    </td>
                    <td className="p-4 border border-gray-200" data-id="element-778">
                      Вища, залежить від процедури
                    </td>
                  </tr>
                  <tr data-id="element-779">
                    <td className="p-4 border border-gray-200 font-medium" data-id="element-780">
                      Час очікування
                    </td>
                    <td className="p-4 border border-gray-200" data-id="element-781">
                      Тижні або місяці
                    </td>
                    <td className="p-4 border border-gray-200" data-id="element-782">
                      Дні (або сьогодні)
                    </td>
                  </tr>
                  <tr data-id="element-783">
                    <td className="p-4 border border-gray-200 font-medium" data-id="element-784">
                      Матеріали
                    </td>
                    <td className="p-4 border border-gray-200" data-id="element-785">
                      Базові (амальгама)
                    </td>
                    <td className="p-4 border border-gray-200" data-id="element-786">
                      Преміум (композит, кераміка)
                    </td>
                  </tr>
                  <tr data-id="element-787">
                    <td className="p-4 border border-gray-200 font-medium" data-id="element-788">
                      Естетика
                    </td>
                    <td className="p-4 border border-gray-200" data-id="element-789">Не пріоритет</td>
                    <td className="p-4 border border-gray-200" data-id="element-790">
                      Високий пріоритет
                    </td>
                  </tr>
                  <tr data-id="element-791">
                    <td className="p-4 border border-gray-200 font-medium" data-id="element-792">
                      Мова
                    </td>
                    <td className="p-4 border border-gray-200" data-id="element-793">Англійська</td>
                    <td className="p-4 border border-gray-200" data-id="element-794">
                      Можлива українська (як у нас)
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>

            <h2 className="text-2xl font-bold text-gray-900 mt-10 mb-6" data-id="element-795">
              Що обрати?
            </h2>
            <p className="mb-6" data-id="element-796">
              <strong data-id="element-797">Обирайте NHS, якщо:</strong>
            </p>
            <ul className="list-disc pl-6 space-y-2 mb-6" data-id="element-798">
              <li data-id="element-799">У вас обмежений бюджет.</li>
              <li data-id="element-800">Вам потрібен лише базовий огляд або просте лікування.</li>
              <li data-id="element-801">Ви готові чекати в черзі.</li>
              <li data-id="element-802">Естетика для вас не головне.</li>
            </ul>
            <p className="mb-6" data-id="element-803">
              <strong data-id="element-804">Обирайте приватну клініку, якщо:</strong>
            </p>
            <ul className="list-disc pl-6 space-y-2 mb-8" data-id="element-805">
              <li data-id="element-806">У вас гострий біль і потрібна допомога сьогодні.</li>
              <li data-id="element-807">Ви хочете якісні білі пломби, коронки або вініри.</li>
              <li data-id="element-808">Вам важливий комфорт, сервіс та сучасне обладнання.</li>
              <li data-id="element-809">Ви хочете спілкуватися з лікарем українською мовою.</li>
            </ul>

            <h2 className="text-2xl font-bold text-gray-900 mt-10 mb-6" data-id="element-810">
              Чому українці обирають Dentico Clinic?
            </h2>
            <p className="mb-6" data-id="element-811">
              Ми — приватна клініка, яка розуміє потреби українців. Ми
              пропонуємо якість, яку не може дати NHS, але за чесними цінами.
              Головна перевага — відсутність мовного бар'єра. Ви точно
              зрозумієте свій діагноз та план лікування.
            </p>
          </div>

          {/* FAQ Section */}
          <div className="mt-16 pt-10 border-t border-gray-100" data-id="element-812">
            <h3 className="text-2xl font-bold text-gray-900 mb-8" data-id="element-813">
              Часті запитання
            </h3>
            <div className="space-y-2" data-id="element-814">
              {faqs.map((faq, idx) => <AccordionItem key={idx} question={faq.q} answer={faq.a} isOpen={openFaqIndex === idx} onToggle={() => toggleFaq(idx)} data-id="element-815" />)}
            </div>
          </div>

          {/* Read Also */}
          <div className="mt-16" data-id="element-816">
            <h3 className="text-2xl font-bold text-gray-900 mb-6" data-id="element-817">
              Читайте також
            </h3>
            <div className="grid sm:grid-cols-2 gap-4" data-id="element-818">
              <Link to="/ua/ukrainskyi-stomatolog-london" data-id="element-819">
                <Card className="p-4 hover:shadow-md transition-shadow h-full flex items-center" data-id="element-820">
                  <span className="text-teal-600 font-medium" data-id="element-821">
                    Український стоматолог у Лондоні
                  </span>
                </Card>
              </Link>
              <Link to="/ua/prices" data-id="element-822">
                <Card className="p-4 hover:shadow-md transition-shadow h-full flex items-center" data-id="element-823">
                  <span className="text-teal-600 font-medium" data-id="element-824">Наші ціни</span>
                </Card>
              </Link>
            </div>
          </div>
        </div>

        {/* CTA */}
        <section className="py-16 bg-teal-900" data-id="element-825">
          <div className="max-w-4xl mx-auto px-4 text-center" data-id="element-826">
            <h2 className="text-3xl font-bold text-white mb-4" data-id="element-827">
              Оберіть якісну стоматологію без компромісів
            </h2>
            <p className="text-xl text-teal-100 mb-8 max-w-2xl mx-auto" data-id="element-828">
              Запишіться на консультацію в Dentico Clinic та отримайте лікування
              європейського рівня.
            </p>
            <div className="flex flex-col sm:flex-row justify-center gap-4" data-id="element-829">
              <a href={BOOKING_URL} target="_blank" rel="noopener noreferrer" className="w-full sm:w-auto" data-id="element-830">
                <Button size="lg" className="w-full gap-2 bg-white text-teal-900 hover:bg-teal-50" data-id="element-831">
                  <Calendar className="w-5 h-5" data-id="element-832" />
                  Записатись онлайн
                </Button>
              </a>
              <a href={WHATSAPP_URL} target="_blank" rel="noopener noreferrer" className="w-full sm:w-auto" data-id="element-833">
                <Button variant="outline" size="lg" className="w-full gap-2 border-teal-400 text-teal-100 hover:bg-teal-800 hover:text-white" data-id="element-834">
                  <MessageCircle className="w-5 h-5" data-id="element-835" />
                  WhatsApp
                </Button>
              </a>
            </div>
          </div>
        </section>
      </article>
    </Layout>;
}